/**
 * Session 快取與 Token 刷新管理
 * 負責 Supabase session 的快取、超時保護、主動 token 刷新
 */

import { supabase } from '@shared/services/core/supabaseClient'
import type { Session } from '@supabase/supabase-js'

// ============ 常數 ============

const CACHE_TTL = 1000 * 60 // 1 分鐘快取
const SESSION_TIMEOUT = 10000 // 10 秒超時

// ============ 服務類別 ============

class SessionCacheService {
  private sessionCache: Session | null = null
  private cacheExpireTime: number | null = null
  private getSessionPromise: Promise<Session | null> | null = null
  private _tokenRefreshInterval: ReturnType<typeof setInterval> | null = null
  private _visibilityHandler: (() => void) | null = null
  private _isRefreshing = false

  async getSession(forceRefresh = false): Promise<Session | null> {
    if (!forceRefresh && this.sessionCache && this.cacheExpireTime && this.cacheExpireTime > Date.now()) {
      return this.sessionCache
    }

    if (this.getSessionPromise) {
      return await this.getSessionPromise
    }

    this.getSessionPromise = this._fetchSession()

    try {
      return await this.getSessionPromise
    } finally {
      this.getSessionPromise = null
    }
  }

  private async _fetchSession(): Promise<Session | null> {
    // H8 — offline returns stale cache instead of null
    if (!navigator.onLine) {
      console.warn('[SessionCache] 離線狀態，回傳快取 session')
      return this.sessionCache  // may be null if never cached, which is correct
    }

    // M11 — store timeoutId so we can clear it after the race
    let timeoutId: ReturnType<typeof setTimeout>
    const sessionPromise = supabase.auth.getSession()
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error(`Session 檢查超時 (${SESSION_TIMEOUT}ms)`)),
        SESSION_TIMEOUT
      )
    })

    try {
      const result = await Promise.race([sessionPromise, timeoutPromise])
      clearTimeout(timeoutId!)
      const session = result.data?.session ?? null

      this.sessionCache = session
      this.cacheExpireTime = Date.now() + CACHE_TTL

      return session
    } catch (error) {
      clearTimeout(timeoutId!)
      const err = error as Error
      console.error('[SessionCache] 獲取 session 失敗:', err.message)

      if (err.message?.includes('超時')) {
        console.warn('[SessionCache] Supabase 連線超時')
      }

      return null
    }
  }

  async refreshToken(): Promise<Session | null> {
    try {
      console.log('[SessionCache] 嘗試刷新 token...')
      const { data, error } = await supabase.auth.refreshSession()

      if (error) {
        console.error('[SessionCache] Token 刷新失敗:', error.message)
        return null
      }

      // H7 — write new session back to cache after refresh
      if (data?.session) {
        this.sessionCache = data.session
        this.cacheExpireTime = Date.now() + CACHE_TTL
        console.log('[SessionCache] Token 刷新成功')
        return data.session
      }

      return null
    } catch (error) {
      console.error('[SessionCache] Token 刷新異常:', error)
      return null
    }
  }

  /**
   * 啟動主動 Token 刷新定時器
   * 每 30 秒檢查 token 是否即將過期，5 分鐘內過期則主動刷新
   * 同時監聽 visibilitychange，切回前台時立即檢查
   */
  startTokenRefreshTimer(): void {
    this.stopTokenRefreshTimer()

    // M10 — concurrent refresh guard for setInterval callback
    this._tokenRefreshInterval = setInterval(async () => {
      if (this._isRefreshing) return
      this._isRefreshing = true
      try { await this._checkAndRefreshToken() } finally { this._isRefreshing = false }
    }, 30 * 1000)

    this._visibilityHandler = async () => {
      if (document.visibilityState === 'visible') {
        if (this._isRefreshing) return
        this._isRefreshing = true
        try { await this._checkAndRefreshToken() } finally { this._isRefreshing = false }
      }
    }
    document.addEventListener('visibilitychange', this._visibilityHandler)

    console.log('[SessionCache] Token 刷新定時器已啟動')
  }

  stopTokenRefreshTimer(): void {
    if (this._tokenRefreshInterval) {
      clearInterval(this._tokenRefreshInterval)
      this._tokenRefreshInterval = null
    }
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler)
      this._visibilityHandler = null
    }
  }

  private async _checkAndRefreshToken(): Promise<void> {
    try {
      const raw = localStorage.getItem('om-auth-token')
      if (!raw) return

      const session = JSON.parse(raw)

      // H9 — validate expires_at type before arithmetic
      if (!session?.expires_at || typeof session.expires_at !== 'number') return

      const expiresIn = session.expires_at * 1000 - Date.now()

      // 5 分鐘內即將過期，或已過期但不超過 30 分鐘
      if (expiresIn < 5 * 60 * 1000 && expiresIn > -30 * 60 * 1000) {
        console.log('[SessionCache] Token 即將過期，主動刷新...', `剩餘 ${(expiresIn / 1000).toFixed(0)} 秒`)
        await this.refreshToken()
      }
    } catch (error) {
      console.error('[SessionCache] Token 刷新檢查失敗:', error)
    }
  }

  clearCache(): void {
    this.sessionCache = null
    this.cacheExpireTime = null
  }

  isCacheValid(): boolean {
    return this.sessionCache !== null && this.cacheExpireTime !== null && this.cacheExpireTime > Date.now()
  }
}

export const sessionCacheService = new SessionCacheService()
export default sessionCacheService
